import type { ApiPlayable, CmsSection, RecommendedUser } from "@hive/sdk";
import { hive } from "./apiClient";

/** Same includes and bounded old-server fallback as the App's meetTheMakers. */
export async function loadRecommendedCreators(isCurrent: () => boolean): Promise<RecommendedUser[]> {
	const users = await hive.users.recommended({ limit: 8, include: ["stats", "viewer", "topWorks"], kinds: ["game", "drama"] });
	if (!isCurrent()) return [];
	const legacy = users.length > 0 && users.every((user) => !("stats" in user) && !("viewer" in user) && !("topWorks" in user));
	if (!legacy) {
		if (users.some((user) => typeof user.viewer?.isFollowing !== "boolean" ||
			!Number.isSafeInteger(user.stats?.followersCount) || user.stats!.followersCount < 0 || !Array.isArray(user.topWorks))) {
			throw new Error("Incomplete creator recommendations");
		}
		return users;
	}
	const result: RecommendedUser[] = [];
	for (let start = 0; start < users.length && isCurrent(); start += 4) {
		const batch = await Promise.all(users.slice(start, start + 4).map(async (user) => {
			try {
				const [profile, works] = await Promise.all([
					hive.users.getProfile(user.handle),
					hive.playables.list({ author: user.handle, status: "PUBLISHED", page: 1, pageSize: 3 }),
				]);
				return { ...user, stats: { followersCount: profile.followersCount }, viewer: { isFollowing: profile.isFollowing },
					topWorks: works.items.filter((work) => ["game", "drama"].includes(work.kind)).slice(0, 3) };
			} catch (error) {
				if ((error as { status?: number })?.status === 404) return null;
				throw error;
			}
		}));
		for (const user of batch) if (user) result.push(user);
	}
	return isCurrent() ? result : [];
}

export interface StarterGames { items: ApiPlayable[]; source: "curated" | "explore" | "popular"; href: string }

/** Preserve the operator's order; never use another private history rail as a recommendation. */
export function selectStarterGames(tags: CmsSection[], explore: CmsSection[]): StarterGames | null {
	const curated = tags.filter((section) => /\bcurated\b|精选/i.test(section.title ?? ""));
	const candidates = [...curated.map((section) => ({ section, source: "curated" as const, href: "/?category=" + encodeURIComponent(section.id) })),
		...explore.filter((section) => section.layout === "HERO").map((section) => ({ section, source: "explore" as const, href: "/?section=" + encodeURIComponent(section.id) }))];
	for (const { section, source, href } of candidates) {
		if (section.layout === "FEED" || section.playHistory) continue;
		const seen = new Set<string>();
		const items = section.items.flatMap((entry) => {
			if (entry.type !== "playable" || entry.playable.kind !== "game" || seen.has(entry.playable.id)) return [];
			seen.add(entry.playable.id);
			return [entry.playable];
		}).slice(0, 6);
		if (items.length) return { items, source, href };
	}
	return null;
}
